/**
 * Platform version routing (PP-6027, rules per PP-3987 / PP-3988).
 *
 * The backend assigns every user a `platform_version`:
 *   1 -> stay here (pandora-react, the old app)
 *   2 -> belongs on the new app (pandora-frontend)
 *
 * We mirror that value into the `platform-version` cookie, which the edge reads
 * to pick the origin. Writing the cookie is the whole mechanism; the reload is
 * just how we ask the edge to re-evaluate it.
 *
 * The cookie is written for both versions on purpose — a version 1 user needs
 * it too, so the edge keeps them here instead of using its default.
 */

import {
  getToken,
  hasTokenExpired,
} from '../services/token-service/token.service'

const COOKIE_NAME = 'platform-version'
const COOKIE_DOMAIN = 'localflirt.com'
const COOKIE_MAX_AGE_DAYS = 365

/**
 * Owned by pandora-frontend — the token key comes from its localflirt theme
 * config, the flag from its `adopt-legacy-session` shim. Hardcoded here because
 * the two apps share no package. If either name changes there the handover
 * below stops working and we fall back to that shim, which is the behaviour we
 * had before this existed.
 */
const NEW_APP_TOKEN_KEY = 'com.localflirt.accessToken'
const NEW_APP_ADOPTED_KEY = 'legacySessionAdopted'

/**
 * Scoped to the brand domain so this app and pandora-frontend (which writes
 * `.localflirt.com`) agree on one cookie. Falls back to host-only on localhost
 * and anywhere else, where a `.localflirt.com` domain would be rejected and the
 * write would silently do nothing.
 */
const getCookieDomain = (): string | undefined =>
  window.location.hostname.endsWith(COOKIE_DOMAIN)
    ? `.${COOKIE_DOMAIN}`
    : undefined

const readCookie = (name: string): string | null => {
  const prefix = `${name}=`

  const match = document.cookie
    .split(';')
    .map(entry => entry.trim())
    .find(entry => entry.indexOf(prefix) === 0)

  return match ? match.substring(prefix.length) : null
}

const writeCookie = (name: string, value: string): void => {
  const expires = new Date(
    Date.now() + COOKIE_MAX_AGE_DAYS * 24 * 60 * 60 * 1000,
  ).toUTCString()
  const domain = getCookieDomain()

  document.cookie = [
    `${name}=${value}`,
    `expires=${expires}`,
    'path=/',
    domain ? `domain=${domain}` : '',
  ]
    .filter(Boolean)
    .join(';')
}

/**
 * Gives pandora-frontend a session before we reload into it.
 *
 * Without this the user reloads into an app that cannot see their session, so
 * it sends them to /login; only then does its shim adopt our token and reload
 * again. The result is a login screen going past moments after they logged in
 * successfully, which reads as a failure.
 *
 * Our own token is left in place: this adds a session rather than moving one,
 * so a user rolled back to version 1 still has theirs.
 */
const handOverSession = (): void => {
  const token = getToken()

  if (!token || hasTokenExpired()) return

  writeCookie(NEW_APP_TOKEN_KEY, token)

  // Cookie writes fail silently when storage is blocked, so confirm this one
  // before setting the flag below. A handover that did not stick must leave
  // the shim free to do its job.
  if (readCookie(NEW_APP_TOKEN_KEY) !== token) return

  try {
    localStorage.setItem(NEW_APP_TOKEN_KEY, token)
    // Our token stays in localStorage, so without this flag the shim would
    // re-adopt it every time the user logs out of the new app and they could
    // never sign out.
    localStorage.setItem(NEW_APP_ADOPTED_KEY, '1')
  } catch {
    // Storage is unavailable. The cookie alone authenticates, and the shim
    // cannot run without localStorage either, so nothing is left unguarded.
  }
}

/**
 * Syncs the `platform-version` cookie with the user's assigned version and,
 * for version 2, reloads so the edge routes them to the new app.
 *
 * Safe to call on every `/me/core` response: once the cookie matches it does
 * nothing, which is what stops the reload from repeating.
 */
export const handlePlatformVersionChange = (
  platformVersion: number | undefined,
): void => {
  if (platformVersion !== 1 && platformVersion !== 2) return

  const nextValue = platformVersion.toString()

  if (readCookie(COOKIE_NAME) === nextValue) return

  writeCookie(COOKIE_NAME, nextValue)

  // The read above is the only thing stopping this from running on every
  // /me/core response, and it can only match once the write has landed. A user
  // who blocks cookies would otherwise reload forever: write silently fails,
  // read still misses, reload, repeat. Leave them working here instead.
  if (readCookie(COOKIE_NAME) !== nextValue) return

  if (platformVersion === 2) {
    handOverSession()
    window.location.reload()
  }
}
